import { useEffect, useState } from "react";

/**
 * Phoenix Flame Cursor 🐦‍🔥
 *
 * Replaces the native cursor with a glowing orange dot + lagging purple/gold
 * ring. Expands to gold on interactive elements; emits a 4-particle spark burst
 * on click. OFF BY DEFAULT — opt-in via the toggle in Profile → Preferences.
 *
 * - Auto-disabled on touch / coarse-pointer devices (phones, tablets)
 * - State persisted in localStorage; cross-tab via `storage` event
 * - Component returns null; all rendering is direct DOM (one dot + one ring +
 *   transient spark particles), avoiding React reconciliation on every frame
 */

const PREF_KEY = "omni_cursor_phoenix";
const PREF_EVENT = "omni:cursor-pref-change";

export function isPhoenixCursorEnabled() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(PREF_KEY) === "1";
}

export function setPhoenixCursorEnabled(on) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PREF_KEY, on ? "1" : "0");
  window.dispatchEvent(new CustomEvent(PREF_EVENT, { detail: !!on }));
}

export default function PhoenixCursor() {
  const [enabled, setEnabled] = useState(isPhoenixCursorEnabled());

  // Sync across tabs + react to in-app toggle
  useEffect(() => {
    const onPref = (e) => setEnabled(!!e.detail);
    const onStorage = (e) => {
      if (e.key === PREF_KEY) setEnabled(e.newValue === "1");
    };
    window.addEventListener(PREF_EVENT, onPref);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(PREF_EVENT, onPref);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    // Touch / coarse-pointer detection — bail without doing anything.
    const fine = window.matchMedia("(pointer: fine)").matches;
    const noHover = window.matchMedia("(hover: none)").matches;
    if (!fine || noHover) return;

    // Inject styles once
    const styleEl = document.createElement("style");
    styleEl.id = "omni-phoenix-cursor-style";
    styleEl.textContent = `
      html.omni-flame-cursor, html.omni-flame-cursor * { cursor: none !important; }
      html.omni-flame-cursor input, html.omni-flame-cursor textarea,
      html.omni-flame-cursor [contenteditable="true"] { cursor: text !important; }

      .omni-cursor-dot {
        position: fixed; top: 0; left: 0; width: 10px; height: 10px;
        margin: -5px 0 0 -5px; border-radius: 50%;
        background: #FF6A1A;
        box-shadow: 0 0 12px 2px rgba(255,106,26,0.85), 0 0 24px 6px rgba(212,175,55,0.35);
        pointer-events: none; z-index: 2147483647;
        transition: width 0.18s ease, height 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
        will-change: transform;
      }
      .omni-cursor-dot.hovering {
        width: 16px; height: 16px; margin: -8px 0 0 -8px;
        background: #D4AF37;
        box-shadow: 0 0 18px 4px rgba(212,175,55,0.95), 0 0 36px 10px rgba(124,58,237,0.4);
      }
      .omni-cursor-ring {
        position: fixed; top: 0; left: 0; width: 34px; height: 34px;
        margin: -17px 0 0 -17px; border-radius: 50%;
        border: 1.5px solid rgba(212,175,55,0.55);
        box-shadow: 0 0 18px rgba(124,58,237,0.35), inset 0 0 8px rgba(212,175,55,0.18);
        pointer-events: none; z-index: 2147483646;
        transition: width 0.22s ease, height 0.22s ease, border-color 0.22s ease, opacity 0.22s ease;
        opacity: 0.85;
        will-change: transform;
      }
      .omni-cursor-ring.hovering {
        width: 52px; height: 52px; margin: -26px 0 0 -26px;
        border-color: rgba(212,175,55,0.95);
        opacity: 1;
      }
      .omni-cursor-ring.pressing { transform-origin: center; }
      .omni-cursor-spark {
        position: fixed; width: 6px; height: 6px; margin: -3px 0 0 -3px;
        border-radius: 50%; background: #D4AF37;
        box-shadow: 0 0 10px rgba(255,106,26,0.9);
        pointer-events: none; z-index: 2147483647;
        animation: omni-spark 0.55s ease-out forwards;
      }
      @keyframes omni-spark {
        0%   { transform: translate(0, 0) scale(1);   opacity: 1; }
        70%  { transform: translate(var(--tx), var(--ty)) scale(0.6); opacity: 0.85; }
        100% { transform: translate(var(--tx), var(--ty)) scale(0);   opacity: 0; }
      }
    `;
    document.head.appendChild(styleEl);
    document.documentElement.classList.add("omni-flame-cursor");

    const dot = document.createElement("div");
    dot.className = "omni-cursor-dot";
    const ring = document.createElement("div");
    ring.className = "omni-cursor-ring";
    document.body.appendChild(ring);
    document.body.appendChild(dot);

    let mx = window.innerWidth / 2,  my = window.innerHeight / 2;
    let dx = mx, dy = my, rx = mx, ry = my;

    const HOVER_SELECTOR =
      "a, button, [role='button'], input:not([type='text']):not([type='email']):not([type='password']):not([type='number']):not([type='search']), select, summary, [data-cursor-target]";

    const onMove = (e) => { mx = e.clientX; my = e.clientY; };
    const onLeave = () => { dot.style.opacity = "0"; ring.style.opacity = "0"; };
    const onEnter = () => { dot.style.opacity = "1"; ring.style.opacity = "0.85"; };
    const onOver = (e) => {
      const target = e.target?.closest?.(HOVER_SELECTOR);
      const hovering = !!target && !target.disabled;
      dot.classList.toggle("hovering", hovering);
      ring.classList.toggle("hovering", hovering);
    };
    const onDown = (e) => {
      // 4-particle spark burst at the click point
      for (let i = 0; i < 4; i++) {
        const spark = document.createElement("div");
        spark.className = "omni-cursor-spark";
        const angle = (Math.PI * 2 * i) / 4 + (Math.random() - 0.5) * 0.6;
        const dist = 22 + Math.random() * 14;
        spark.style.setProperty("--tx", `${Math.cos(angle) * dist}px`);
        spark.style.setProperty("--ty", `${Math.sin(angle) * dist}px`);
        spark.style.left = `${e.clientX}px`;
        spark.style.top = `${e.clientY}px`;
        document.body.appendChild(spark);
        setTimeout(() => spark.remove(), 600);
      }
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mousedown", onDown, { passive: true });
    document.addEventListener("mouseover", onOver, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    let raf;
    const tick = () => {
      // Dot is responsive (0.45 lerp); ring lags for trail effect (0.16 lerp)
      dx += (mx - dx) * 0.45;
      dy += (my - dy) * 0.45;
      rx += (mx - rx) * 0.16;
      ry += (my - ry) * 0.16;
      dot.style.transform = `translate(${dx}px, ${dy}px)`;
      ring.style.transform = `translate(${rx}px, ${ry}px)`;
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      dot.remove();
      ring.remove();
      styleEl.remove();
      document.documentElement.classList.remove("omni-flame-cursor");
      // Sweep stray sparks
      document.querySelectorAll(".omni-cursor-spark").forEach((s) => s.remove());
    };
  }, [enabled]);

  return null;
}
